import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  Download as DownloadIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { portalApi, PortalDokument } from '../../services/portalService';

const kategorien = ['Beleg', 'Rechnung', 'Vertrag', 'Quittung', 'Sonstiges'];

const PortalDokumentePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dokumente, setDokumente] = useState<PortalDokument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitel, setUploadTitel] = useState('');
  const [uploadKategorie, setUploadKategorie] = useState('Beleg');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadDokumente();
  }, []);

  // Check for upload query param to open dialog
  useEffect(() => {
    if (searchParams.get('upload') === 'true') {
      setUploadOpen(true);
      // Remove the query param from URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const loadDokumente = async () => {
    try {
      setLoading(true);
      const response = await portalApi.getMeineDokumente();
      setDokumente(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (dok: PortalDokument) => {
    try {
      const response = await portalApi.downloadDokument(dok.portalDokumentID);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', dok.dateiName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitel) return;

    try {
      setUploading(true);
      await portalApi.uploadDokument(uploadFile, uploadTitel, undefined, uploadKategorie);
      setUploadOpen(false);
      setUploadFile(null);
      setUploadTitel('');
      loadDokumente();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Meine Dokumente</Typography>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setUploadOpen(true)}
        >
          Dokument einreichen
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Titel</TableCell>
              <TableCell>Kategorie</TableCell>
              <TableCell>Dateiname</TableCell>
              <TableCell>Grösse</TableCell>
              <TableCell>Datum</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dokumente.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Keine Dokumente vorhanden
                </TableCell>
              </TableRow>
            ) : (
              dokumente.map((dok) => (
                <TableRow key={dok.portalDokumentID}>
                  <TableCell>{dok.titel}</TableCell>
                  <TableCell>
                    <Chip size="small" label={dok.kategorie} />
                  </TableCell>
                  <TableCell>{dok.dateiName}</TableCell>
                  <TableCell>{formatFileSize(dok.dateiGroesse)}</TableCell>
                  <TableCell>
                    {new Date(dok.freigegebenAm || dok.hochgeladenAm || '').toLocaleDateString('de-CH')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={dok.vomPortalHochgeladen ? 'Eingereicht' : 'Bereitgestellt'}
                      color={dok.vomPortalHochgeladen ? 'info' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDownload(dok)} title="Herunterladen">
                      <DownloadIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dokument einreichen</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Titel"
              value={uploadTitel}
              onChange={(e) => setUploadTitel(e.target.value)}
              fullWidth
              required
            />
            <TextField
              select
              label="Kategorie"
              value={uploadKategorie}
              onChange={(e) => setUploadKategorie(e.target.value)}
              fullWidth
            >
              {kategorien.map((kat) => (
                <MenuItem key={kat} value={kat}>
                  {kat}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" component="label">
              {uploadFile ? uploadFile.name : 'Datei auswählen'}
              <input
                type="file"
                hidden
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!uploadFile || !uploadTitel || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Hochladen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalDokumentePage;
